import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import _ from 'lodash';
import VacAvsCMSModel from '@/visa-application/models/VacAvsCMSModel';
import DirectusService from '@/core/directus/directus.service';
import LanguageCode from '@/locale/language-code';
import JhiAlertService from '@/shared/alert/alert.service';
import ApplicationCenterHomeCMSModel, { FloatingPromptModelCMSModel } from '@/visa-application/models/ApplicationCenterHomeCMSModel';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';
import AddedValueServiceCMSModel from '@/visa-application/models/AddedValueServiceCMSModel';
import KeywordsCMSModel, { Keywords } from '@/visa-application/models/KeywordsCMSModel';
import ResidenceCountryCMSModel from '@/visa-application/models/ResidenceCountryCMSModel';
import ServicesCMSModel from '@/visa-application/models/ServicesCMSModel';
import { filterDataByCountryAndIssuer, defineCanonicalTag } from '@/shared/utils/utils';
import BannerCarouselCMSModel from '@/visa-application/models/BannerCarouselCMSModel';
import { ALL_COUNTRY_CODE, ON_DEMAND } from '@/constants';
import CurrencyCMSModel from '@/visa-application/models/CurrencyCMSModel';

@Component
export default class ApplicationCenterHome extends Vue {
  public applicationCenterHomeCmsData: ApplicationCenterHomeCMSModel = new ApplicationCenterHomeCMSModel();
  public avsList: VacAvsCMSModel[] = [];
  public applicationCenterCMSModel: ApplicationCenterCMSModel = new ApplicationCenterCMSModel();
  public floatingPromptCMSData: FloatingPromptModelCMSModel = new FloatingPromptModelCMSModel();
  public showPopup = false;
  public currentLanguage;
  public keywords: Partial<Keywords> = {};
  public residenceCountryCMSData: ResidenceCountryCMSModel = new ResidenceCountryCMSModel();
  public servicesCmsData: ServicesCMSModel[] = [];
  public bannerCarousel: BannerCarouselCMSModel[] = [];
  public swiperOption = {};
  public onDemandCurrency: CurrencyCMSModel = new CurrencyCMSModel();
  public onDemand = ON_DEMAND;
  public emptyNotice = 'n_a';

  @Inject('directusService') private directusService: () => DirectusService;
  @Inject('alertService') private alertService: () => JhiAlertService;

  private residenceCountryCode: string;
  private issuer: string;
  public moreAvsInfoData: AddedValueServiceCMSModel = new AddedValueServiceCMSModel();

  public beforeRouteEnter(to, from, next) {
    defineCanonicalTag(to.params.residenceCountryCode, to.params.issuer, to.params.formGroupId, to.fullPath);

    next(vm => {
      if (to.params.residenceCountryCode === 'ab') {
        vm.$store.commit('metaInfo', { ...vm.$store.getters.metaInfo, meta_robots: 'noindex, nofollow' });
      }

      if (to.params.residenceCountryCode && to.params.issuer) {
        vm.residenceCountryCode = to.params.residenceCountryCode;
        vm.issuer = to.params.issuer;
        vm.onCurrentLanguageChanged(vm.$store.getters.currentLanguage);
      }
    });
  }

  beforeRouteLeave(to, from, next) {
    next(vm => {
      if (from.params.residenceCountryCode === 'ab') {
        vm.$store.commit('metaInfo', { ...vm.$store.getters.metaInfo, meta_robots: '' });
      }
    });
  }

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onCurrentLanguageChanged(language: LanguageCode): void {
    this.getResidenceCountryContent(language);
    this.getContentFromCMS(language);
    this.getAvailableAvsListFromCMS(language);
    this.getSpecialNewsFromCMS(language);
    this.getServicesContent();
    this.keywords = this.$store.getters.keywords;
    this.currentLanguage = language;
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  public getContentFromCMS(currentLanguage: LanguageCode): void {
    const filter = {
      'country.code': {
        in: [this.residenceCountryCode, ALL_COUNTRY_CODE]
      }
    };
    this.directusService()
      .getCollection<ApplicationCenterHomeCMSModel>(
        ApplicationCenterHomeCMSModel,
        'application_center_home',
        ['*.*', 'translation.*.*'],
        filter,
        currentLanguage
      )
      .then((response: ApplicationCenterHomeCMSModel[]) => {
        if (response.length > 0) {
          const cmsDataByCountryCodeAll = response.find(item => item.country.code === ALL_COUNTRY_CODE);
          const cmsDataByCountryCode = response.find(item => item.country.code === this.residenceCountryCode);
          this.applicationCenterHomeCmsData = cmsDataByCountryCode ? cmsDataByCountryCode : cmsDataByCountryCodeAll;
          this.applicationCenterHomeCmsData.visa_flow_first_step_img = cmsDataByCountryCode.visa_flow_first_step_img
            ? cmsDataByCountryCode.visa_flow_first_step_img
            : cmsDataByCountryCodeAll.visa_flow_first_step_img;
          this.applicationCenterHomeCmsData.visa_flow_second_step_img = cmsDataByCountryCode.visa_flow_second_step_img
            ? cmsDataByCountryCode.visa_flow_second_step_img
            : cmsDataByCountryCodeAll.visa_flow_second_step_img;
          this.applicationCenterHomeCmsData.visa_flow_third_step_img = cmsDataByCountryCode.visa_flow_third_step_img
            ? cmsDataByCountryCode.visa_flow_third_step_img
            : cmsDataByCountryCodeAll.visa_flow_third_step_img;
          this.$store.commit('metaInfo', this.applicationCenterHomeCmsData);
        }
      })
      .catch(error => console.error(error));

    const filters = {
      'issuer.code': {
        eq: this.issuer
      }
    };
    this.directusService()
      .getCollection<BannerCarouselCMSModel>(
        BannerCarouselCMSModel,
        'banner',
        ['translation.carousel.*', 'translation.carousel_mobile.*', 'swipe_autoplay_time'],
        filters,
        currentLanguage
      )
      .then((response: BannerCarouselCMSModel[]) => {
        this.bannerCarousel = response;
        // 设置轮播时间
        // Set the rotation time
        if (Number(this.bannerCarousel[0].swipeAutoplayTime)) {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: Number(this.bannerCarousel[0].swipeAutoplayTime) * 1000,
              disableOnInteraction: false
            }
          };
        } else {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: 10000,
              disableOnInteraction: false
            }
          };
        }
      })
      .catch(error => console.error(error));

    this.directusService()
      .getCollection(
        FloatingPromptModelCMSModel,
        'application_centers',
        ['translation.floating_prompt'],
        { code: { eq: this.issuer } },
        currentLanguage
      )
      .then((response: FloatingPromptModelCMSModel[]) => {
        this.floatingPromptCMSData = response[0];
      })
      .catch(error => console.error(error));
  }

  public getSpecialNewsFromCMS(language: LanguageCode): void {
    const filters = {
      code: {
        eq: this.issuer
      },
      'country.code': {
        eq: this.residenceCountryCode
      }
    };
    this.directusService()
      .getCollection<ApplicationCenterCMSModel>(
        ApplicationCenterCMSModel,
        'application_centers',
        [
          'translation.special_news',
          'translation.floating_prompt',
          'visa_application_assistance_link',
          'translation.visa_application_assistance_title',
          'translation.visa_application_assistance_button'
        ],
        filters,
        language
      )
      .then((response: ApplicationCenterCMSModel[]) => {
        this.applicationCenterCMSModel = response[0];
      })
      .catch(error => console.error(error));
  }

  public startVisaApplication(routerUrl): void {
    if (routerUrl.startsWith('http')) {
      window.location.href = routerUrl;
    } else if (routerUrl.endsWith(`/${this.residenceCountryCode}/${this.issuer}`)) {
      this.$router.push('/' + routerUrl);
    } else {
      this.$router.push(`/${routerUrl}/${this.residenceCountryCode}/${this.issuer}`);
    }
  }

  public getAvailableAvsListFromCMS(currentLanguage: LanguageCode): void {
    const filter = {
      'vac.code': {
        eq: this.issuer
      }
    };
    this.directusService()
      .getCollection<VacAvsCMSModel>(VacAvsCMSModel, 'vac_avs', ['*.*.*'], filter, currentLanguage)
      .then((result: VacAvsCMSModel[]) => {
        this.avsList = _.filter(_.sortBy(result, 'weight'), 'is_active')
          .reverse()
          .slice(0, 3);
      })
      .catch(error => {
        console.error(error);
      });

    this.directusService()
      .getCollection<CurrencyCMSModel>(CurrencyCMSModel, 'currencies', ['*.*'], undefined, currentLanguage)
      .then((res: CurrencyCMSModel[]) => {
        this.onDemandCurrency = res.find(currency => currency.code === this.onDemand);
      })
      .catch(error => console.error(error));
  }

  public redirectToOpeningHours(): void {
    this.$router.push({
      name: 'OpeningHours',
      params: {
        residenceCountryCode: this.residenceCountryCode,
        issuer: this.issuer
      }
    });
  }

  public closeAvsPopup(): void {
    this.showPopup = false;
    this.moreAvsInfoData = new AddedValueServiceCMSModel();
  }

  public onAVSMoreInfoClick(avs: VacAvsCMSModel): void {
    this.moreAvsInfoData = avs.avs;
    this.moreAvsInfoData.name = avs.name ? avs.name : avs.avs.name;
    this.moreAvsInfoData.long_description = avs.longDescription ? avs.longDescription : avs.avs.long_description;
    this.moreAvsInfoData.more_info_url = avs.more_info_url ? avs.more_info_url : avs.avs.more_info_url;
    this.moreAvsInfoData.video_url = avs.video_url ? avs.video_url : avs.avs.video_url;
    if (this.moreAvsInfoData.more_info_url === null || this.moreAvsInfoData.more_info_url === '') {
      this.showPopup = true;
    } else if (this.moreAvsInfoData.more_info_url && this.moreAvsInfoData.more_info_url.startsWith('http')) {
      window.location.href = this.moreAvsInfoData.more_info_url;
    } else if (this.moreAvsInfoData.more_info_url && this.moreAvsInfoData.more_info_url.length > 0) {
      this.showPopup = false;
      this.$router.push(`/${this.moreAvsInfoData.more_info_url}/${this.residenceCountryCode}/${this.issuer}`);
    }
  }

  private getResidenceCountryContent(currentLanguage: LanguageCode) {
    this.directusService()
      .getCollection<ResidenceCountryCMSModel>(
        ResidenceCountryCMSModel,
        'residence_country',
        ['country_flag.*', 'country_image.*'],
        undefined,
        currentLanguage
      )
      .then((response: ResidenceCountryCMSModel[]) => {
        this.residenceCountryCMSData = response[0];
      })
      .catch(error => console.error(error));
  }

  public getServicesContent(): void {
    const filter = {
      available_from: {
        lte: 'now'
      }
    };
    this.directusService()
      .getCollection<ServicesCMSModel>(
        ServicesCMSModel,
        'services',
        ['*', 'icon.*', 'translation.*'],
        filter,
        this.$store.getters.currentLanguage,
        '-weight'
      )
      .then((response: ServicesCMSModel[]) => {
        response = filterDataByCountryAndIssuer(this.issuer, this.residenceCountryCode, response);
        if (response.length > 0) {
          this.servicesCmsData = _.uniqBy(response, 'name');
        }
      })
      .catch(error => error);
  }

  public goToServiceProcessPage(service: ServicesCMSModel): void {
    this.$router.push({
      name: 'ServicesProcess',
      params: {
        serviceName: service.name,
        residenceCountryCode: this.residenceCountryCode,
        issuer: this.issuer
      }
    });
  }
}
